<template>
  <div class="soar-container flex justify-between items-center flex-col gap-y-2 pt-2 mb-3">
    <RouterLink
      :to="{ name: homeRoute }"
      class="banners w-full flex justify-between items-center h-[107px]"
      dir="ltr"
    >
      <template v-if="themeStore.currentScheme == 'light'">
        <img src="@/assets/pngs/icalogo.png" alt="icalogo" class="w-72 lg:w-1/2 h-auto" />
        <img src="@/assets/pngs/uaelog.png" alt="uaelog" class="w-10 lg:w-20 h-auto" />
      </template>
      <template v-else>
        <img src="@/assets/pngs/icalogo-dark.png" alt="icalogo" class="w-72 lg:w-1/2 h-auto" />
        <img src="@/assets/pngs/uaelog-dark.png" alt="uaelog" class="w-10 lg:w-20 h-auto" />
      </template>
    </RouterLink>
    <div class="print:hidden w-full flex justify-between items-center">
      <div class="flex gap-x-3 items-center">
        <MobilemenuTrigger class="block lg:hidden pt-2"></MobilemenuTrigger>
        <RouterLink :to="{ name: homeRoute }" class="block lg:hidden">
          <FontAwesomeIcon
            :icon="faHomeAlt"
            class="text-primary text-2xl hover:scale-[1.02] active:scale-100"
          />
        </RouterLink>
        <small class="hidden md:block text-primary">Version: {{ versionNum }}</small>
      </div>
      <div class="flex gap-x-4 items-center">
        <div class="relative flex gap-x-4 items-center pe-3 border-e border-e-primary">
          <button
            :key="i18n.locale"
            class="flex justify-start items-center gap-x-2"
            @click="toggleLocale"
          >
            <faGlobeAsiaIcon class="w-4 fill-primary/80 hover:fill-primary" />
            <span v-if="i18n.locale.value == 'en'" class="text-primary">العربية</span>
            <span v-else class="text-primary">English</span>
          </button>
          <button v-tippy="{ content: $t('Settings') }" @click="onSettingsClick">
            <FontAwesomeIcon
              :icon="faCog"
              class="text-primary hover:rotate-45 transition-all duration-100"
            />
          </button>
          <NotificationMenu v-tippy="{ content: $t('Notifications') }"></NotificationMenu>
          <MenuPopper
            :trigger-label="authStore.profileName"
            trigger-classes=" text-start text-primary font-semibold text-xs"
          >
            <template #menu="{ hide }">
              <div class="w-full flex flex-col gap-y-1">
                <RouterLink
                  v-for="(menu, key) in profileMenus"
                  :key="key"
                  :to="menu.path"
                  @click.prevent.stop="hide()"
                  class="px-3 py-1.5 text-start text-sm text-primary hover:text-primary-700 hover:bg-gray-200"
                >
                  {{ $t(menu.label) }}
                </RouterLink>
              </div>
            </template>
          </MenuPopper>
        </div>
        <a class="text-primary font-semibold" :href="authStore?.UAEPassLogoutLink">
          {{ $t('Logout') }}
        </a>
      </div>
    </div>
    <div class="w-full hidden lg:block">
      <div class="w-full flex justify-between">
        <nav class="w-full flex gap-x-6 items-center">
          <RouterLink to="/">
            <FontAwesomeIcon
              :icon="faHomeAlt"
              class="text-primary text-2xl hover:scale-[1.02] active:scale-100"
            />
          </RouterLink>
          <div
            class="relative flex-1 flex gap-x-6 gap-y-3 flex-wrap items-center text-sm text-start"
          >
            <template v-for="(menu, key) in navMenu" :key="key">
              <MenuPopper
                v-if="Object.keys(menu).includes('children')"
                :trigger-label="$t(menu.label)"
                :menu="menu.children"
              ></MenuPopper>
              <RouterLink v-else :to="{ path: menu.path, query: menu.query }">
                {{ $t(menu.label) }}
              </RouterLink>
            </template>
            <UsermanualMenu />
          </div>
        </nav>
      </div>
      <!-- <div v-if="route.name != 'home'" class="breadcrumbs w-full flex gap-x-2">
        <router-link :to="{ name: homeRoute }">{{ $t('Home') }}</router-link>
        <div class="text-sm">/</div>
        <template :key="key" v-for="(i, key) in crumbs">
          <div class="flex gap-x-2">
            <span> {{ i.label }}</span>
            <div class="text-sm">/</div>
          </div>
        </template>
      </div> -->
    </div>
  </div>
</template>

<script setup>
import faGlobeAsiaIcon from '@/assets/icons/faGlobeAsia.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import establishmentNavMenus from '@/assets/json/nav/establishment/menu.json'
import establishmentProfileMenus from '@/assets/json/nav/establishment/profile.json'
import individualNavMenus from '@/assets/json/nav/individual/menu.json'
import individualProfileMenus from '@/assets/json/nav/individual/profile.json'
import typingCenterNavMenus from '@/assets/json/nav/typing_center/menu.json'
import typingCenterProfileMenus from '@/assets/json/nav/typing_center/profile.json'
import MenuPopper from '@/components/Buttons/MenuPopper.vue'
import NotificationMenu from '@/components/Buttons/NotificationMenu.vue'
import MobilemenuTrigger from '@/components/headers/mobile_menu/MenuTrigger.vue'
import UsermanualMenu from '@/components/headers/shared/UsermanualMenu.vue'
import useStringFunctions from '@/composables/useStringFunctions'
import apiHelperService from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useGlobalStore } from '@/stores/global'
import { useThemeStore } from '@/stores/theme'
import { faCog, faHomeAlt } from '@fortawesome/free-solid-svg-icons'
import { useCookies } from '@vueuse/integrations/useCookies'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { userTypeLandings } from '@/router'

const themeStore = useThemeStore()

const i18n = useI18n()
const globalStore = useGlobalStore()
const route = useRoute()
const authStore = useAuthStore()
const versionNum = ref('')

const toggleLocale = () => {
  if (i18n.locale.value == 'en') {
    i18n.locale.value = 'ar'
  } else {
    i18n.locale.value = 'en'
  }

  onLocaleChange()
}

const homeRoute = computed(() => {
  const { applicationId } = authStore.user
  return userTypeLandings[applicationId]
})

const navMenu = computed(() => {
  const { applicationId } = authStore.user
  const {
    application: { establishment, TypingCenter }
  } = client_lookups

  if (applicationId == establishment) {
    return establishmentNavMenus
  }

  if (applicationId == TypingCenter) {
    return typingCenterNavMenus
  }

  return individualNavMenus
})

const profileMenus = computed(() => {
  const { applicationId } = authStore.user

  const {
    application: { establishment, TypingCenter }
  } = client_lookups

  if (applicationId == establishment) {
    return establishmentProfileMenus
  }

  if (applicationId == TypingCenter) {
    return typingCenterProfileMenus
  }

  return individualProfileMenus
})

const crumbs = computed(() => {
  const matchedRoutes = route.matched

  return matchedRoutes
    .filter((r) => r.meta.breadcrumb?.hidden != true)
    .map((routeItem) => {
      /**
       * dynamically set the breadcrumb path
       */
      if (routeItem.name == 'ServicesGrid') {
        const category = route.params.category

        return {
          label: useStringFunctions(category).kebabToTitle(),
          to: getRoutePath(route, routeItem)
        }
      }

      return {
        label: i18n.t(routeItem.meta?.breadcrumb?.label || routeItem.name),
        to: getRoutePath(route, routeItem)
      }
    })
})

const getRoutePath = (route, routeItem) => {
  const matchedSegments = route.matched.slice(0, route.matched.indexOf(routeItem) + 1)
  return matchedSegments.map((segment) => segment.path).join('/')
}

const onSettingsClick = () => {
  globalStore.$patch({ endbarState: true })
}

const onLocaleChange = async () => {
  await nextTick()
  const locale = i18n.locale.value
  globalStore.languageId = locale == 'ar' ? 1 : 2

  const cookies = useCookies(['locale'])
  cookies.set('i18n_redirected', locale)

  const html = document.querySelector('html')
  html.setAttribute('lang', locale)
  html.setAttribute('dir', locale == 'ar' ? 'rtl' : 'ltr')
}

const getVersionNumber = async () => {
  const res = await apiHelperService('/api/content/environment')
  versionNum.value = res.version
}

onMounted(async () => {
  await getVersionNumber()
})
</script>

<style lang="scss" scoped></style>
